import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Mail, Lock, User, ChevronRight, Loader2, Building2, FileCheck, Clock, CheckCircle2, Shield } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import PhoneInput from './PhoneInput';

// Goal options with icons
const GOALS = [
  { id: 'weight_loss', icon: '⚖️', en: 'Weight Loss', es: 'Pérdida de Peso', ru: 'Снижение веса' },
  { id: 'muscle_building', icon: '💪', en: 'Muscle Building', es: 'Construcción Muscular', ru: 'Набор мышечной массы' },
  { id: 'anti_aging', icon: '✨', en: 'Anti-Aging', es: 'Anti-Envejecimiento', ru: 'Антивозрастной уход' },
  { id: 'cognitive_enhancement', icon: '🧠', en: 'Cognitive Enhancement', es: 'Mejora Cognitiva', ru: 'Улучшение когнитивных функций' },
  { id: 'recovery_healing', icon: '🩹', en: 'Recovery & Healing', es: 'Recuperación', ru: 'Восстановление и заживление' },
  { id: 'immune_support', icon: '🛡️', en: 'Immune Support', es: 'Apoyo Inmunológico', ru: 'Поддержка иммунитета' },
  { id: 'hormonal_balance', icon: '⚡', en: 'Hormonal Balance', es: 'Equilibrio Hormonal', ru: 'Гормональный баланс' },
  { id: 'skin_health', icon: '🌸', en: 'Skin Health', es: 'Salud de la Piel', ru: 'Здоровье кожи' }
];

// Organization types
const ORG_TYPES = [
  { id: 'laboratory', en: 'Research Laboratory', es: 'Laboratorio de Investigación', ru: 'Исследовательская лаборатория' },
  { id: 'clinic', en: 'Medical Clinic', es: 'Clínica Médica', ru: 'Медицинская клиника' },
  { id: 'hospital', en: 'Hospital', es: 'Hospital', ru: 'Больница' },
  { id: 'university', en: 'University/Academic', es: 'Universidad/Académico', ru: 'Университет/научное учреждение' },
  { id: 'pharmaceutical', en: 'Pharmaceutical Company', es: 'Empresa Farmacéutica', ru: 'Фармацевтическая компания' },
  { id: 'veterinary', en: 'Veterinary Practice', es: 'Práctica Veterinaria', ru: 'Ветеринарная клиника' },
  { id: 'other', en: 'Other Licensed Entity', es: 'Otra Entidad Autorizada', ru: 'Другое аккредитованное учреждение' }
];

const AuthPage = () => {
  const [mode, setMode] = useState('login'); // 'login', 'register'
  const [step, setStep] = useState(1); // For multi-step registration (1: Personal, 2: Professional, 3: Address, 4: Goals)
  
  // Personal info
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Professional info
  const [organizationType, setOrganizationType] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [specialty, setSpecialty] = useState('');
  
  // Shipping address
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('Spain');
  
  // Goals
  const [selectedGoals, setSelectedGoals] = useState([]);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const { loginWithGoogle, loginWithGoogleCredential, loginWithEmail, register, isAuthenticated } = useAuth();
  const { currentLanguage, t: tg } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const params = new URLSearchParams(location.search);
      const redirect = params.get('redirect') || '/';
      navigate(redirect, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const result = await loginWithEmail(email, password);
    
    if (result.success) {
      const params = new URLSearchParams(location.search);
      const redirect = params.get('redirect') || '/';
      navigate(redirect);
    } else {
      setError(result.error);
    }
    setIsLoading(false);
  };

  const handleRegister = async () => {
    setIsLoading(true);
    setError('');

    const fullName = `${firstName} ${lastName}`.trim();
    const result = await register(email, password, fullName, selectedGoals, {
      phone,
      organization_type: organizationType,
      organization_name: organizationName,
      license_number: licenseNumber,
      specialty,
      address_line1: addressLine1,
      address_line2: addressLine2,
      city,
      state,
      postal_code: postalCode,
      country
    });
    
    if (result.success) {
      navigate('/recommendations');
    } else {
      setError(result.error);
    }
    setIsLoading(false);
  };

  const validateStep1 = () => {
    if (!firstName || !lastName || !email || !phone || !password || !confirmPassword) {
      setError(t.fillAllFields);
      return false;
    }
    if (password !== confirmPassword) {
      setError(t.passwordMismatch);
      return false;
    }
    if (password.length < 6) {
      setError(t.passwordTooShort);
      return false;
    }
    setError('');
    return true;
  };

  const validateStep2 = () => {
    if (!organizationType || !organizationName) {
      setError(t.fillAllFields);
      return false;
    }
    setError('');
    return true;
  };

  const validateStep3 = () => {
    if (!addressLine1 || !city || !postalCode || !country) {
      setError(t.fillAllFields);
      return false;
    }
    setError('');
    return true;
  };

  const toggleGoal = (goalId) => {
    setSelectedGoals(prev => 
      prev.includes(goalId) 
        ? prev.filter(g => g !== goalId)
        : [...prev, goalId]
    );
  };

  const getGoalName = (goal) => goal[currentLanguage] || goal.en;
  const getOrgName = (org) => org[currentLanguage] || org.en;

  const translations = {
    en: {
      login: 'Login',
      register: 'Request Your Account',
      registerSubtitle: 'To ensure compliance and quality care, we exclusively supply licensed medical professionals with high-quality research compounds. Please complete the form below with your credentials for verification.',
      verificationNote: 'Our team will review your submission, and once approved, you\'ll gain access to our full product catalog and exclusive pricing. Verification is typically completed within 24-48 hours.',
      email: 'Email/Login',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      firstName: 'First Name',
      lastName: 'Last Name',
      phone: 'Phone',
      continueWith: 'Or continue with',
      noAccount: "Don't have an account?",
      haveAccount: 'Already have an account?',
      signUp: 'Request Account',
      signIn: 'Sign In',
      next: 'Next',
      back: 'Back',
      submit: 'Submit Application',
      // Steps
      step1: 'Personal Information',
      step2: 'Professional Details',
      step3: 'Shipping Address',
      step4: 'Research Interests',
      // Professional
      organizationType: 'Organization Type',
      selectOrgType: 'Select organization type...',
      organizationName: 'Organization/Institution Name',
      licenseNumber: 'License/Registration Number',
      specialty: 'Specialty/Department',
      // Address
      addressLine1: 'Address Line 1',
      addressLine2: 'Address Line 2 (Optional)',
      city: 'City',
      state: 'State/Province',
      postalCode: 'Postal Code',
      country: 'Country',
      // Goals
      selectGoals: 'Research Interests',
      selectGoalsDesc: 'Select your areas of research interest (optional)',
      // Terms
      termsTitle: 'Terms & Conditions',
      termsText: 'I confirm that I represent an authorized entity with the legal permission to purchase research compounds. I understand all products are for research purposes only.',
      // Errors
      fillAllFields: 'Please fill in all required fields',
      passwordMismatch: 'Passwords do not match',
      passwordTooShort: 'Password must be at least 6 characters',
      google: 'Continue with Google'
    },
    es: {
      login: 'Iniciar Sesión',
      register: 'Solicite Su Cuenta',
      registerSubtitle: 'Para garantizar el cumplimiento y la atención de calidad, suministramos exclusivamente a profesionales médicos autorizados con compuestos de investigación de alta calidad. Complete el formulario a continuación con sus credenciales para verificación.',
      verificationNote: 'Nuestro equipo revisará su solicitud y, una vez aprobada, tendrá acceso a nuestro catálogo completo y precios exclusivos. La verificación se completa normalmente en 24-48 horas.',
      email: 'Correo/Login',
      password: 'Contraseña',
      confirmPassword: 'Confirmar Contraseña',
      firstName: 'Nombre',
      lastName: 'Apellido',
      phone: 'Teléfono',
      continueWith: 'O continuar con',
      noAccount: '¿No tiene cuenta?',
      haveAccount: '¿Ya tiene cuenta?',
      signUp: 'Solicitar Cuenta',
      signIn: 'Iniciar Sesión',
      next: 'Siguiente',
      back: 'Atrás',
      submit: 'Enviar Solicitud',
      // Steps
      step1: 'Información Personal',
      step2: 'Datos Profesionales',
      step3: 'Dirección de Envío',
      step4: 'Intereses de Investigación',
      // Professional
      organizationType: 'Tipo de Organización',
      selectOrgType: 'Seleccione tipo de organización...',
      organizationName: 'Nombre de Organización/Institución',
      licenseNumber: 'Número de Licencia/Registro',
      specialty: 'Especialidad/Departamento',
      // Address
      addressLine1: 'Dirección Línea 1',
      addressLine2: 'Dirección Línea 2 (Opcional)',
      city: 'Ciudad',
      state: 'Provincia',
      postalCode: 'Código Postal',
      country: 'País',
      // Goals
      selectGoals: 'Intereses de Investigación',
      selectGoalsDesc: 'Seleccione sus áreas de interés de investigación (opcional)',
      // Terms
      termsTitle: 'Términos y Condiciones',
      termsText: 'Confirmo que represento a una entidad autorizada con permiso legal para comprar compuestos de investigación. Entiendo que todos los productos son solo para fines de investigación.',
      // Errors
      fillAllFields: 'Por favor complete todos los campos requeridos',
      passwordMismatch: 'Las contraseñas no coinciden',
      passwordTooShort: 'La contraseña debe tener al menos 6 caracteres',
      google: 'Continuar con Google'
    },
    ru: {
      login: 'Вход',
      register: 'Запросить аккаунт',
      registerSubtitle: 'Для обеспечения соответствия требованиям и качественного обслуживания мы осуществляем поставки исключительно лицензированным медицинским специалистам. Пожалуйста, заполните форму ниже для верификации.',
      verificationNote: 'Наша команда рассмотрит вашу заявку, и после одобрения вы получите доступ к полному каталогу и эксклюзивным ценам. Верификация обычно занимает 24-48 часов.',
      email: 'Электронная почта',
      password: 'Пароль',
      confirmPassword: 'Подтвердите пароль',
      firstName: 'Имя',
      lastName: 'Фамилия',
      phone: 'Телефон',
      continueWith: 'Или войти через',
      noAccount: 'Нет аккаунта?',
      haveAccount: 'Уже есть аккаунт?',
      signUp: 'Запросить аккаунт',
      signIn: 'Войти',
      next: 'Далее',
      back: 'Назад',
      submit: 'Отправить заявку',
      // Steps
      step1: 'Личные данные',
      step2: 'Профессиональные данные',
      step3: 'Адрес доставки',
      step4: 'Области исследований',
      // Professional
      organizationType: 'Тип организации',
      selectOrgType: 'Выберите тип организации...',
      organizationName: 'Название организации/учреждения',
      licenseNumber: 'Номер лицензии/регистрации',
      specialty: 'Специализация/отдел',
      // Address
      addressLine1: 'Адрес (строка 1)',
      addressLine2: 'Адрес (строка 2, необязательно)',
      city: 'Город',
      state: 'Область/регион',
      postalCode: 'Почтовый индекс',
      country: 'Страна',
      // Goals
      selectGoals: 'Области исследований',
      selectGoalsDesc: 'Выберите интересующие вас направления (необязательно)',
      // Terms
      termsTitle: 'Условия и положения',
      termsText: 'Я подтверждаю, что представляю аккредитованное учреждение с законным правом на приобретение исследовательских соединений. Я понимаю, что вся продукция предназначена исключительно для научных исследований.',
      // Errors
      fillAllFields: 'Пожалуйста, заполните все обязательные поля',
      passwordMismatch: 'Пароли не совпадают',
      passwordTooShort: 'Пароль должен содержать не менее 6 символов',
      google: 'Войти через Google'
    }
  };

  const t = translations[currentLanguage] || translations.en;

  // Registration steps
  const renderRegistrationStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">{t.firstName} *</Label>
                <Input
                  id="firstName"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="John"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">{t.lastName} *</Label>
                <Input
                  id="lastName"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Doe"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t.email} *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  placeholder="you@organization.com"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t.phone} *</Label>
              <PhoneInput
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                name="phone"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="password">{t.password} *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10"
                    placeholder="••••••••"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t.confirmPassword} *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10"
                    placeholder="••••••••"
                    required
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t.organizationType} *</Label>
              <select
                value={organizationType}
                onChange={(e) => setOrganizationType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-capsule-teal"
                required
              >
                <option value="">{t.selectOrgType}</option>
                {ORG_TYPES.map(org => (
                  <option key={org.id} value={org.id}>{getOrgName(org)}</option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="organizationName">{t.organizationName} *</Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  id="organizationName"
                  type="text"
                  value={organizationName}
                  onChange={(e) => setOrganizationName(e.target.value)}
                  className="pl-10"
                  placeholder={tg('auth_research_institute_placeholder')}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="licenseNumber">{t.licenseNumber}</Label>
              <div className="relative">
                <FileCheck className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  id="licenseNumber"
                  type="text"
                  value={licenseNumber}
                  onChange={(e) => setLicenseNumber(e.target.value)}
                  className="pl-10"
                  placeholder="Optional"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="specialty">{t.specialty}</Label>
              <Input
                id="specialty"
                type="text"
                value={specialty}
                onChange={(e) => setSpecialty(e.target.value)}
                placeholder="e.g., Biochemistry, Endocrinology"
              />
            </div>
          </div>
        );

      case 3:
        // Shipping Address
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="addressLine1">{t.addressLine1} *</Label>
              <Input
                id="addressLine1"
                type="text"
                value={addressLine1}
                onChange={(e) => setAddressLine1(e.target.value)}
                placeholder={tg('address_street_address')}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="addressLine2">{t.addressLine2}</Label>
              <Input
                id="addressLine2"
                type="text"
                value={addressLine2}
                onChange={(e) => setAddressLine2(e.target.value)}
                placeholder={tg('address_apartment_suite')}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">{t.city} *</Label>
                <Input
                  id="city"
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Madrid"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">{t.state}</Label>
                <Input
                  id="state"
                  type="text"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  placeholder="Madrid"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="postalCode">{t.postalCode} *</Label>
                <Input
                  id="postalCode"
                  type="text"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  placeholder="28001"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="country">{t.country} *</Label>
                <Input
                  id="country"
                  type="text"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  placeholder="Spain"
                  required
                />
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <p className="text-gray-600 text-sm">{t.selectGoalsDesc}</p>
            <div className="grid grid-cols-2 gap-3">
              {GOALS.map((goal) => (
                <button
                  key={goal.id}
                  type="button"
                  onClick={() => toggleGoal(goal.id)}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${
                    selectedGoals.includes(goal.id)
                      ? 'border-capsule-teal bg-capsule-teal/10 shadow-md'
                      : 'border-gray-200 hover:border-capsule-teal hover:bg-capsule-teal/5'
                  }`}
                >
                  <span className="text-2xl mb-1 block">{goal.icon}</span>
                  <span className="text-sm font-medium text-gray-900">{getGoalName(goal)}</span>
                </button>
              ))}
            </div>

            <div className="pt-4 border-t">
              <label className="flex items-start gap-3 cursor-pointer p-4 border-2 border-capsule-teal/30 rounded-2xl hover:bg-capsule-teal/5">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1 w-5 h-5 text-capsule-teal border-gray-300 rounded focus:ring-capsule-teal"
                />
                <div>
                  <p className="font-medium text-gray-900">{t.termsTitle}</p>
                  <p className="text-sm text-gray-600 mt-1">{t.termsText}</p>
                </div>
              </label>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-capsule-bg">
      <Header />
      
      <main className="container mx-auto px-4 py-12">
        <div className="max-w-lg mx-auto">
          {mode === 'login' ? (
            // Login Screen
            <Card className="shadow-xl border-0">
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-2xl font-bold text-gray-900">{t.login}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                    {error}
                  </div>
                )}

                {/* Custom Google OAuth Login - Shows "PenLab Peptide" branding */}
                {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
                <div className="flex justify-center">
                  <GoogleLogin
                    onSuccess={async (credentialResponse) => {
                      setIsLoading(true);
                      setError('');
                      const result = await loginWithGoogleCredential(credentialResponse.credential);
                      if (result.success) {
                        const params = new URLSearchParams(location.search);
                        const redirect = params.get('redirect') || '/';
                        navigate(redirect);
                      } else {
                        setError(result.error || 'Google login failed');
                      }
                      setIsLoading(false);
                    }}
                    onError={() => {
                      setError('Google login failed. Please try again.');
                    }}
                    text="continue_with"
                    shape="rectangular"
                    width="300"
                    theme="outline"
                  />
                </div>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-gray-500">{t.continueWith}</span>
                  </div>
                </div>

                <form onSubmit={handleEmailLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="loginEmail">{t.email}</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        id="loginEmail"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10"
                        placeholder="you@example.com"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="loginPassword">{t.password}</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        id="loginPassword"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10"
                        placeholder="••••••••"
                        required
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-capsule-teal to-capsule-aqua hover:from-capsule-teal-dark hover:to-capsule-teal rounded-2xl"
                    disabled={isLoading}
                  >
                    {isLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    {t.signIn}
                  </Button>
                </form>

                <p className="text-center text-sm text-gray-600">
                  {t.noAccount}{' '}
                  <button onClick={() => setMode('register')} className="text-capsule-teal hover:text-capsule-teal-dark font-medium">
                    {t.signUp}
                  </button>
                </p>
              </CardContent>
            </Card>
          ) : (
            // Registration Screen (Multi-step)
            <Card className="shadow-xl border-0">
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-2xl font-bold text-gray-900">{t.register}</CardTitle>
                <p className="text-gray-600 text-sm mt-2">{t.registerSubtitle}</p>
                
                {/* Verification notice */}
                <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl text-left">
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-800">{t.verificationNote}</p>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-6">
                {/* Step indicator */}
                <div className="flex items-center justify-between">
                  {[1, 2, 3, 4].map((s) => (
                    <React.Fragment key={s}>
                      <div className="flex flex-col items-center">
                        <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-sm ${
                          step >= s ? 'bg-capsule-teal text-white' : 'bg-gray-200 text-gray-500'
                        }`}>
                          {step > s ? <CheckCircle2 className="w-4 h-4" /> : s}
                        </div>
                        <span className="text-xs mt-1 text-gray-500 text-center hidden sm:block">
                          {s === 1 ? t.step1 : s === 2 ? t.step2 : s === 3 ? t.step3 : t.step4}
                        </span>
                      </div>
                      {s < 4 && <div className={`flex-1 h-1 mx-1 sm:mx-2 ${step > s ? 'bg-capsule-teal' : 'bg-gray-200'}`} />}
                    </React.Fragment>
                  ))}
                </div>

                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                    {error}
                  </div>
                )}

                {renderRegistrationStep()}

                <div className="flex gap-3">
                  {step > 1 && (
                    <Button variant="outline" onClick={() => setStep(step - 1)} className="flex-1">
                      {t.back}
                    </Button>
                  )}
                  
                  {step < 4 ? (
                    <Button
                      onClick={() => {
                        if (step === 1 && validateStep1()) setStep(2);
                        else if (step === 2 && validateStep2()) setStep(3);
                        else if (step === 3 && validateStep3()) setStep(4);
                      }}
                      className="flex-1 bg-gradient-to-r from-capsule-teal to-capsule-aqua hover:from-capsule-teal-dark hover:to-capsule-teal rounded-2xl"
                    >
                      {t.next}
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  ) : (
                    <Button
                      onClick={handleRegister}
                      disabled={!agreedToTerms || isLoading}
                      className="flex-1 bg-gradient-to-r from-capsule-teal to-capsule-aqua hover:from-capsule-teal-dark hover:to-capsule-teal rounded-2xl disabled:opacity-50"
                    >
                      {isLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                      <Shield className="w-4 h-4 mr-2" />
                      {t.submit}
                    </Button>
                  )}
                </div>

                <p className="text-center text-sm text-gray-600">
                  {t.haveAccount}{' '}
                  <button onClick={() => { setMode('login'); setStep(1); }} className="text-capsule-teal hover:text-capsule-teal-dark font-medium">
                    {t.signIn}
                  </button>
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AuthPage;
